import React, { useState, useMemo } from 'react';
import {
  DollarSign,
  Calendar,
  Download,
  Printer,
  CheckCircle2,
  Clock,
  TrendingUp,
  CreditCard,
  FileText,
  Truck
} from 'lucide-react';
import { useAreaMotorista } from '../../contexts/AreaMotoristaContext';
import { formatDateBR } from '../../utils/dateUtils';

export const AreaMotoristaExtrato: React.FC = () => {
  const { currentMotorista, stats, ordensServico, isAgregado, isLoading } = useAreaMotorista();

  const [periodo, setPeriodo] = useState<'mes_atual' | 'mes_anterior' | 'todos'>('mes_atual');

  const extratoFiltrado = useMemo(() => {
    const hoje = new Date();
    const mesAtual = hoje.getMonth();
    const anoAtual = hoje.getFullYear();

    return ordensServico.filter((os) => {
      if (!os.data_atendimento) return true;
      const data = new Date(os.data_atendimento);

      if (periodo === 'mes_atual') {
        return data.getMonth() === mesAtual && data.getFullYear() === anoAtual;
      } else if (periodo === 'mes_anterior') {
        const mesAnt = mesAtual === 0 ? 11 : mesAtual - 1;
        const anoAnt = mesAtual === 0 ? anoAtual - 1 : anoAtual;
        return data.getMonth() === mesAnt && data.getFullYear() === anoAnt;
      }
      return true;
    });
  }, [ordensServico, periodo]);

  const totaisCalculados = useMemo(() => {
    let repasses = 0;
    let despesas = 0;
    let concluidas = 0;

    extratoFiltrado.forEach((os) => {
      repasses += Number(os.repasse) || 0;
      despesas += Number(os.valor_despesa) || 0;
      if (os.status_id === 4 || os.assinatura) {
        concluidas++;
      }
    });

    return {
      totalBruto: repasses,
      totalDespesas: despesas,
      totalLiquido: repasses - despesas,
      qtdViagens: extratoFiltrado.length,
      qtdConcluidas: concluidas
    };
  }, [extratoFiltrado]);

  const handlePrint = () => {
    window.print();
  };

  if (!isAgregado) {
    return (
      <div className="space-y-6 text-left">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-slate-500" />
              <span>Extrato Financeiro & Repasses</span>
            </h1>
            <p className="text-xs text-slate-400">
              Informação de remuneração e regime de trabalho.
            </p>
          </div>
        </div>

        <div className="p-8 sm:p-12 text-center rounded-3xl border border-slate-800 bg-slate-950/70 space-y-4 max-w-xl mx-auto shadow-2xl">
          <div className="w-16 h-16 rounded-2xl bg-blue-500/10 text-blue-400 flex items-center justify-center mx-auto border border-blue-500/20">
            <Truck className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-bold text-white">Extrato de Repasse Indisponível</h2>
            <p className="text-xs text-slate-400 leading-relaxed">
              O seu cadastro atual está classificado como <strong className="text-white">Motorista da Frota Própria (CLT)</strong>. 
              Por política de segurança da Voope, os valores de repasse por viagem são visíveis exclusivamente para parceiros <strong className="text-purple-400">Agregados / Autônomos</strong>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-purple-400" />
            <span>Extrato Financeiro & Repasses</span>
          </h1>
          <p className="text-xs text-slate-400">
            Acompanhe o fechamento de fretes, diárias e valores recebidos.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-bold flex items-center gap-1.5 transition shadow"
          >
            <Printer className="w-4 h-4" />
            <span>Imprimir Extrato</span>
          </button>
        </div>
      </div>

      {/* Financial Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        
        {/* Total Líquido */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-950/60 via-slate-900 to-slate-950 border border-purple-800/40 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-purple-300 uppercase tracking-wider">Total a Receber</span>
            <div className="w-7 h-7 rounded-lg bg-purple-500/20 text-purple-300 flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black text-white font-mono">
            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totaisCalculados.totalLiquido)}
          </p>
          <span className="text-[10px] text-purple-400/80">Calculado para o período selecionado</span>
        </div>

        {/* Viagens Concluídas */}
        <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Viagens Concluídas</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black text-emerald-400">
            {totaisCalculados.qtdConcluidas} <span className="text-xs font-normal text-slate-500">/ {totaisCalculados.qtdViagens} viagens</span>
          </p>
          <span className="text-[10px] text-slate-500">Com assinatura registrada</span>
        </div>

        {/* Média por Viagem */}
        <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Média por Frete</span>
            <div className="w-7 h-7 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center">
              <Truck className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black text-blue-300 font-mono">
            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
              totaisCalculados.qtdViagens > 0 ? totaisCalculados.totalLiquido / totaisCalculados.qtdViagens : 0
            )}
          </p>
          <span className="text-[10px] text-slate-500">Ticket médio por serviço</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        {[
          { id: 'mes_atual', label: 'Este Mês' },
          { id: 'mes_anterior', label: 'Mês Anterior' },
          { id: 'todos', label: 'Histórico Completo' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setPeriodo(tab.id as any)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
              periodo === tab.id
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Statement Table / Cards */}
      <div className="rounded-3xl border border-slate-800 bg-slate-950/70 overflow-hidden shadow-xl">
        <div className="p-4 sm:p-5 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <FileText className="w-4 h-4 text-purple-400" />
            <span>Demonstrativo de Fretes</span>
          </h2>
          <span className="text-xs text-slate-400">
            {extratoFiltrado.length} registros
          </span>
        </div>

        {isLoading ? (
          <div className="p-12 text-center text-xs text-slate-500">Carregando extrato financeiro...</div>
        ) : extratoFiltrado.length === 0 ? (
          <div className="p-12 text-center text-xs text-slate-500 space-y-2">
            <DollarSign className="w-8 h-8 mx-auto text-slate-600 stroke-[1.5]" />
            <p>Nenhum lançamento financeiro para o período selecionado.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800/80">
            {extratoFiltrado.map((os) => {
              const isFinalizado = os.status_id === 4 || !!os.assinatura;
              const repasse = Number(os.repasse) || 0;
              const despesa = Number(os.valor_despesa) || 0;
              const liquido = repasse - despesa;

              return (
                <div
                  key={os.id}
                  className="p-4 sm:p-5 hover:bg-slate-900/60 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                >
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-black text-white">OS #{os.id}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          isFinalizado
                            ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                            : 'bg-amber-950 text-amber-300 border border-amber-800'
                        }`}
                      >
                        {isFinalizado ? 'Aprovado / Finalizado' : 'Em Aberto'}
                      </span>
                      {os.data_atendimento && (
                        <span className="text-slate-500 text-[11px]">
                          {formatDateBR(os.data_atendimento)}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-300 font-medium truncate">
                      {os.descricao || 'Transporte de Cargas'}
                    </p>
                    <p className="text-slate-500 text-[11px] truncate">
                      🏁 {os.entrega || 'Destino padrão'}
                    </p>
                  </div>

                  <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-1 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider sm:block hidden">
                      Valor Líquido
                    </span>
                    <span className="text-sm sm:text-base font-black font-mono text-emerald-400">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(liquido)}
                    </span>
                    {despesa > 0 && (
                      <span className="text-[10px] text-red-400">
                        Despesas: -{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(despesa)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PIX / Repasse Info Box */}
      <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
        <div className="space-y-0.5">
          <span className="font-bold text-white flex items-center gap-1.5">
            <CreditCard className="w-4 h-4 text-purple-400" />
            Dados para Depósito / Chave PIX
          </span>
          <p className="text-slate-400 text-[11px]">
            Os repasses são efetuados conforme o calendário de pagamentos da Voope Transportes.
          </p>
        </div>
        <div className="text-right">
          <span className="text-[11px] font-mono text-purple-300 font-bold bg-purple-950/50 px-3 py-1.5 rounded-xl border border-purple-800">
            {currentMotorista?.chave_pix || currentMotorista?.documento || 'Chave PIX: CPF'}
          </span>
        </div>
      </div>

    </div>
  );
};
